import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus,
  FileText,
  ArrowLeft,
  X,
  Loader
} from 'lucide-react';
import { useInquiryLists } from '../../hooks/useInquiryLists';
import { supabase } from '../../supabaseClient';
import { showNotification, showError } from '../../utils/notificationHandler';

/**
 * AddToInquiryModal Component
 * 
 * Modal for adding products to inquiry lists (RFQ).
 * Allows users to either create a new inquiry list or add to an existing draft list.
 * 
 * @param {boolean} isOpen - Whether the modal is open
 * @param {function} onClose - Callback to close the modal
 * @param {object} productData - Product information to add
 * @param {string} productData.partNumber - Part number
 * @param {string} productData.description - Product description
 * @param {number} productData.price - Product price
 * @param {string} productData.stockStatus - Stock availability status
 * @param {string} userId - Current user ID
 * @param {function} onSuccess - Optional callback after successful add (receives listId)
 */
const AddToInquiryModal = ({
  isOpen,
  onClose,
  productData,
  userId,
  onSuccess
}) => {
  const history = useHistory();
  const { inquiryLists, createList } = useInquiryLists(userId);
  
  // Modal state
  const [step, setStep] = useState('choose'); // 'choose', 'create', 'select'
  const [selectedListId, setSelectedListId] = useState(null);
  const [newListName, setNewListName] = useState('');
  const [newListDescription, setNewListDescription] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [notes, setNotes] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  // Get draft inquiry lists
  const draftLists = inquiryLists.filter(list => list.status === 'draft');

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setStep('choose');
      setSelectedListId(null);
      setNewListName('');
      setNewListDescription('');
      setQuantity(1);
      setNotes('');
      setIsProcessing(false);
    }
  }, [isOpen]);

  // Helper function to add item to a list
  const addItemToList = async (listId) => {
    if (!productData) {
      throw new Error('Product data is required');
    }

    const { error } = await supabase.rpc('add_inquiry_item', {
      inquiry_list_id: listId,
      part_number: productData.partNumber,
      description: productData.description,
      quantity: quantity,
      notes: notes || `Stock Status: ${productData.stockStatus || 'Unknown'}`
    });

    if (error) throw error;
  };

  // Handle creating new list and adding item
  const handleCreateAndAdd = async () => {
    if (!newListName.trim()) {
      showError('Please enter a list name');
      return;
    }

    try {
      setIsProcessing(true);

      // Create the list
      const listData = await createList(newListName, newListDescription || null);
      const newListId = listData?.id || listData;

      if (!newListId) {
        throw new Error('Failed to get list ID from created list');
      }

      // Add item to the new list
      await addItemToList(newListId);

      showNotification(`Created "${newListName}" and added item successfully`);
      
      // Call success callback or navigate
      if (onSuccess) {
        onSuccess(newListId);
      } else {
        // Ask user if they want to view the list
        const shouldNavigate = window.confirm('Item added to inquiry list. Would you like to view the list?');
        if (shouldNavigate) {
          history.push(`/user/inquiry-list/${newListId}`);
        }
      }

      onClose();
    } catch (error) {
      console.error('Error creating list and adding item:', error);
      showError('Failed to create inquiry list: ' + error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle adding to existing list
  const handleAddToExisting = async (listId) => {
    try {
      setIsProcessing(true);

      // Add item to the selected list
      await addItemToList(listId);

      // Get list name for notification
      const list = inquiryLists.find(l => l.id === listId);
      const listName = list?.list_name || 'inquiry list';

      showNotification(`Added item to "${listName}" successfully`);

      // Call success callback or navigate
      if (onSuccess) {
        onSuccess(listId);
      } else {
        // Ask user if they want to view the list
        const shouldNavigate = window.confirm('Item added to inquiry list. Would you like to view the list?');
        if (shouldNavigate) {
          history.push(`/user/inquiry-list/${listId}`);
        }
      }

      onClose();
    } catch (error) {
      console.error('Error adding item to list:', error);
      showError('Failed to add item to inquiry list: ' + error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">
              {step === 'choose' && 'Add to Inquiry List'}
              {step === 'create' && 'Create New Inquiry List'}
              {step === 'select' && 'Select Inquiry List'}
            </h2>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
              disabled={isProcessing}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Product Info */}
          {productData && (
            <div className="mb-6 p-4 bg-blue-50 rounded-lg">
              <div className="text-sm text-blue-800">
                <div className="font-semibold">{productData.partNumber}</div>
                <div className="text-blue-700 mt-1">{productData.description}</div>
                {productData.stockStatus && (
                  <div className="text-blue-600 mt-1 text-xs">
                    Stock Status: {productData.stockStatus}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 1: Choose Action */}
          {step === 'choose' && (
            <div className="space-y-4">
              <p className="text-gray-600 mb-4">Choose how you want to proceed:</p>

              {/* Create New List Option */}
              <button
                onClick={() => {
                  setStep('create');
                  setNewListName(`Inquiry - ${new Date().toLocaleDateString('en-AU')}`);
                }}
                className="w-full p-4 border-2 border-blue-600 rounded-lg hover:bg-blue-50 transition-colors text-left"
              >
                <div className="flex items-center gap-3">
                  <Plus className="text-blue-600 flex-shrink-0" size={24} />
                  <div>
                    <div className="font-semibold text-gray-900">Create New Inquiry List</div>
                    <div className="text-sm text-gray-600">Start a fresh inquiry list with this item</div>
                  </div>
                </div>
              </button>

              {/* Add to Existing List Option */}
              {draftLists.length > 0 && (
                <button
                  onClick={() => setStep('select')}
                  className="w-full p-4 border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-left"
                >
                  <div className="flex items-center gap-3">
                    <FileText className="text-gray-600 flex-shrink-0" size={24} />
                    <div>
                      <div className="font-semibold text-gray-900">Add to Existing List</div>
                      <div className="text-sm text-gray-600">
                        Add to one of your {draftLists.length} draft inquiry list{draftLists.length !== 1 ? 's' : ''}
                      </div>
                    </div>
                  </div>
                </button>
              )}

              <button
                onClick={onClose}
                className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          )}

          {/* Step 2a: Create New List */}
          {step === 'create' && (
            <div className="space-y-4">
              <button
                onClick={() => {
                  setStep('choose');
                  setNewListName('');
                  setNewListDescription('');
                }}
                disabled={isProcessing}
                className="text-blue-600 hover:text-blue-700 text-sm flex items-center gap-1"
              >
                <ArrowLeft size={16} />
                Back
              </button>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  List Name *
                </label>
                <input
                  type="text"
                  value={newListName}
                  onChange={(e) => setNewListName(e.target.value)}
                  placeholder="e.g., Hydraulic Parts Q1 2026"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  autoFocus
                  disabled={isProcessing}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description (Optional)
                </label>
                <textarea
                  value={newListDescription}
                  onChange={(e) => setNewListDescription(e.target.value)}
                  placeholder="Add details about this inquiry list..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  disabled={isProcessing}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Quantity
                </label>
                <input
                  type="number"
                  min="1"
                  value={quantity}
                  onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={isProcessing}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes (Optional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Any special requirements or notes..."
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  disabled={isProcessing}
                />
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => {
                    setStep('choose');
                    setNewListName('');
                    setNewListDescription('');
                  }}
                  disabled={isProcessing}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateAndAdd}
                  disabled={!newListName.trim() || isProcessing}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {isProcessing ? (
                    <>
                      <Loader className="w-4 h-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    'Create & Add Item'
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Step 2b: Select Existing List */}
          {step === 'select' && (
            <div className="space-y-4">
              <button
                onClick={() => setStep('choose')}
                disabled={isProcessing}
                className="text-blue-600 hover:text-blue-700 text-sm flex items-center gap-1"
              >
                <ArrowLeft size={16} />
                Back
              </button>

              <p className="text-gray-600">Select a draft inquiry list:</p>

              <div className="space-y-2 max-h-60 overflow-y-auto">
                {draftLists.map(list => (
                  <button
                    key={list.id}
                    onClick={() => setSelectedListId(list.id)}
                    disabled={isProcessing}
                    className={`w-full p-4 border rounded-lg transition-colors text-left ${
                      selectedListId === list.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-300 hover:bg-gray-50 hover:border-blue-500'
                    } disabled:opacity-50`}
                  >
                    <div className="font-semibold text-gray-900">{list.list_name}</div>
                    {list.description && (
                      <div className="text-sm text-gray-600 mt-1">{list.description}</div>
                    )}
                    <div className="text-sm text-gray-500 mt-2">
                      {list.item_count} item{list.item_count !== 1 ? 's' : ''} • Created: {formatDate(list.created_at)}
                    </div>
                  </button>
                ))}
              </div>

              {selectedListId && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Quantity
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={quantity}
                      onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      disabled={isProcessing}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Notes (Optional)
                    </label>
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Any special requirements or notes..."
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                      disabled={isProcessing}
                    />
                  </div>
                </>
              )}

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setStep('choose')}
                  disabled={isProcessing}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleAddToExisting(selectedListId)}
                  disabled={!selectedListId || isProcessing}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {isProcessing ? (
                    <>
                      <Loader className="w-4 h-4 mr-2 animate-spin" />
                      Adding...
                    </>
                  ) : (
                    'Add to List'
                  )}
                </button>
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AddToInquiryModal;
